/**
 * Share a card somebody is chasing, or one they just caught.
 *
 * The link points at the catalogue card rather than at anyone's collection
 * row, because /card/:cardId is guest browsable - whoever it is sent to can
 * open it without an account, which is the only kind of link worth sending.
 * Binder sharing already has its own module; this is the single-card case.
 */

import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { SHARE_ORIGIN } from './binderShare';

/** The guest-browsable catalogue route for a wish row or a caught card. */
export function cardShareUrl({ setId, cardId }) {
  if (!setId || !cardId) return null;
  const localId = String(cardId).replace(`${setId}-`, '');
  return `${SHARE_ORIGIN}/card/tcg_${setId}_${localId}`;
}

// Closing the share sheet without picking anything is not a failure.
const cancelled = (e) => /cancel|abort/i.test(String(e?.message || e?.errorMessage || e?.name || ''));

/**
 * Open the share sheet for one card.
 *
 * @param {Object} card { setId, cardId, name, caught }
 * @returns {Promise<{ url: string, method: string }>} method is share, clipboard or cancelled
 */
export async function shareCard({ setId, cardId, name, caught = false }) {
  const url = cardShareUrl({ setId, cardId });
  if (!url) throw new Error('That card has no catalogue link to share.');
  const title = name ? `${name} on CardChase` : 'A card on CardChase';
  const text = caught
    ? `I just caught ${name || 'a card'} off my chase list on CardChase`
    : `${name || 'This card'} is on my chase list on CardChase`;

  if (Capacitor.isNativePlatform()) {
    try { await Share.share({ title, text, url, dialogTitle: 'Share card' }); }
    catch (e) { if (!cancelled(e)) throw e; return { url, method: 'cancelled' }; }
    return { url, method: 'share' };
  }
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try { await navigator.share({ title, text, url }); }
    catch (e) { if (!cancelled(e)) throw e; return { url, method: 'cancelled' }; }
    return { url, method: 'share' };
  }
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
  }
  return { url, method: 'clipboard' };
}
